import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/apiClient';
import { splitQuestionsForApplyPreview } from '../utils/applyFormFieldOrder';
import './ApplicationQuestionsManager.css';

const ApplicationQuestionsManager = ({
    jobId,
    jobTitle,
    showResumeField = true,
    resumeAfterPreResumeFields = false,
    onQuestionsChange,
}) => {
    const [questions, setQuestions] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [mode, setMode] = useState('create'); // 'create' or 'preview'
    
    const questionTypes = [
        { value: 'text', label: 'Short Answer' },
        { value: 'number', label: 'Number' },
        { value: 'multiple_choice', label: 'Multiple Choice' },
        { value: 'dropdown', label: 'Dropdown' },
        { value: 'checkbox', label: 'Checkboxes' }
    ];

    const { preQuestions, postQuestions, orderedQuestions } = useMemo(() => {
        const { pre, post } = splitQuestionsForApplyPreview(questions);
        return {
            preQuestions: pre,
            postQuestions: post,
            orderedQuestions: [...pre, ...post],
        };
    }, [questions]);

    useEffect(() => {
        fetchQuestions();
    }, [jobId]);

    const fetchQuestions = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions`, {
            });
            if (response.ok) {
                const data = await response.json();
                setQuestions(data);
                if (onQuestionsChange) onQuestionsChange(data);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
        }
    };

    const handleAddQuestion = async (questionData) => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(questionData)
            });
            
            if (response.ok) {
                fetchQuestions();
                setShowAddForm(false);
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to create question');
            }
        } catch (error) {
            console.error('Error creating question:', error);
            alert('Error creating question');
        }
    };

    const handleUpdateQuestion = async (questionId, questionData) => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions/${questionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(questionData)
            });
            
            if (response.ok) {
                fetchQuestions();
                setEditingQuestion(null);
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to update question');
            }
        } catch (error) {
            console.error('Error updating question:', error);
            alert('Error updating question');
        }
    };

    const handleDelete = async (questionId) => {
        if (!window.confirm('Delete this question?')) return;
        
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions/${questionId}`, {
                method: 'DELETE',
            });
            
            if (response.ok) {
                fetchQuestions();
            } else {
                alert('Failed to delete question');
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            alert('Error deleting question');
        }
    };

    const handleMoveUp = async (index) => {
        if (index === 0) return;
        
        const newQuestions = [...orderedQuestions];
        [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
        
        const orders = newQuestions.map((q, i) => ({ id: q.id, order: i }));
        
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions/reorder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders })
            });
            
            if (response.ok) {
                fetchQuestions();
            }
        } catch (error) {
            console.error('Error reordering:', error);
        }
    };

    const handleMoveDown = async (index) => {
        if (index === orderedQuestions.length - 1) return;
        
        const newQuestions = [...orderedQuestions];
        [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
        
        const orders = newQuestions.map((q, i) => ({ id: q.id, order: i }));
        
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions/reorder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders })
            });
            
            if (response.ok) {
                fetchQuestions();
            }
        } catch (error) {
            console.error('Error reordering:', error);
        }
    };

    return (
        <div className="application-questions-manager">
            <div className="questions-header">
                <h3>Application Questions</h3>
                <div className="mode-toggle">
                    <button 
                        className={mode === 'create' ? 'active' : ''}
                        onClick={() => setMode('create')}
                    >
                        Create/Edit
                    </button>
                    <button 
                        className={mode === 'preview' ? 'active' : ''}
                        onClick={() => setMode('preview')}
                    >
                        Preview
                    </button>
                </div>
            </div>

            {mode === 'preview' ? (
                <ApplicationFormPreview 
                    jobId={jobId} 
                    jobTitle={jobTitle}
                    questions={orderedQuestions}
                />
            ) : (
                <>
                    {showAddForm && (
                        <QuestionForm
                            onSubmit={handleAddQuestion}
                            onCancel={() => setShowAddForm(false)}
                            questionTypes={questionTypes}
                        />
                    )}

                    <div className="questions-list">
                        {questions.length === 0 ? (
                            <div className="no-questions">
                                <p>Loading standard application questions…</p>
                            </div>
                        ) : resumeAfterPreResumeFields ? (
                            <>
                                <p className="aqm-section-label">Before résumé</p>
                                {preQuestions.map((q) => {
                                    const listIndex = orderedQuestions.findIndex((x) => x.id === q.id);
                                    return (
                                    <QuestionItem
                                        key={q.id}
                                        question={q}
                                        index={listIndex}
                                        onDelete={handleDelete}
                                        onEdit={setEditingQuestion}
                                        onMoveUp={handleMoveUp}
                                        onMoveDown={handleMoveDown}
                                        questionTypes={questionTypes}
                                        canMoveUp={listIndex > 0}
                                        canMoveDown={listIndex < orderedQuestions.length - 1}
                                    />
                                    );
                                })}
                                {showResumeField && (
                                    <div className="resume-field-info">
                                        <div className="info-box">
                                            <strong>Resume Upload</strong> — required on the candidate form after name, email, phone, city &amp; state
                                        </div>
                                    </div>
                                )}
                                <p className="aqm-section-label">After résumé</p>
                                {postQuestions.map((q) => {
                                    const listIndex = orderedQuestions.findIndex((x) => x.id === q.id);
                                    return (
                                        <QuestionItem
                                            key={q.id}
                                            question={q}
                                            index={listIndex}
                                            onDelete={handleDelete}
                                            onEdit={setEditingQuestion}
                                            onMoveUp={handleMoveUp}
                                            onMoveDown={handleMoveDown}
                                            questionTypes={questionTypes}
                                            canMoveUp={listIndex > 0}
                                            canMoveDown={listIndex < orderedQuestions.length - 1}
                                        />
                                    );
                                })}
                            </>
                        ) : (
                            <>
                                {showResumeField && (
                                    <div className="resume-field-info">
                                        <div className="info-box">
                                            <strong>Resume Upload</strong> - Always required (automatically included in form)
                                        </div>
                                    </div>
                                )}
                                {orderedQuestions.map((q, index) => (
                                    <QuestionItem
                                        key={q.id}
                                        question={q}
                                        index={index}
                                        onDelete={handleDelete}
                                        onEdit={setEditingQuestion}
                                        onMoveUp={handleMoveUp}
                                        onMoveDown={handleMoveDown}
                                        questionTypes={questionTypes}
                                        canMoveUp={index > 0}
                                        canMoveDown={index < orderedQuestions.length - 1}
                                    />
                                ))}
                            </>
                        )}
                    </div>

                    {!showAddForm && !editingQuestion && (
                        <button 
                            className="add-question-button"
                            onClick={() => setShowAddForm(true)}
                        >
                            + Add Question
                        </button>
                    )}

                    {editingQuestion && (
                        <QuestionForm
                            question={editingQuestion}
                            onSubmit={(data) => handleUpdateQuestion(editingQuestion.id, data)}
                            onCancel={() => setEditingQuestion(null)}
                            questionTypes={questionTypes}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const QuestionForm = ({ question, onSubmit, onCancel, questionTypes }) => {
    const [formData, setFormData] = useState({
        question_text: question?.question_text || '',
        question_type: question?.question_type || 'text',
        is_required: question?.is_required || false,
        placeholder: question?.placeholder || '',
        help_text: question?.help_text || '',
        options: question?.options || []
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const data = { ...formData };
        
        // Only include options if type requires it
        if (!['multiple_choice', 'checkbox', 'dropdown'].includes(data.question_type)) {
            data.options = null;
        } else if (data.options.length === 0) {
            alert('Please add at least one option');
            return;
        }
        
        onSubmit(data);
    };

    const addOption = () => {
        setFormData({
            ...formData,
            options: [...formData.options, '']
        });
    };

    const updateOption = (index, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = value;
        setFormData({ ...formData, options: newOptions });
    };

    const removeOption = (index) => {
        const newOptions = formData.options.filter((_, i) => i !== index);
        setFormData({ ...formData, options: newOptions });
    };

    return (
        <form onSubmit={handleSubmit} className="question-form">
            <div className="form-group">
                <label>Question Text *</label>
                <input
                    type="text"
                    value={formData.question_text}
                    onChange={(e) => setFormData({...formData, question_text: e.target.value})}
                    required
                    placeholder="e.g., Years of experience?"
                />
            </div>

            <div className="form-group">
                <label>Question Type *</label>
                <select
                    value={formData.question_type}
                    onChange={(e) => setFormData({...formData, question_type: e.target.value})}
                    required
                >
                    {questionTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </select>
            </div>

            {['multiple_choice', 'checkbox', 'dropdown'].includes(formData.question_type) && (
                <div className="form-group">
                    <label>Options * (at least 2 required)</label>
                    {formData.options.map((option, index) => (
                        <div key={index} className="option-input">
                            <input
                                type="text"
                                value={option}
                                onChange={(e) => updateOption(index, e.target.value)}
                                placeholder={`Option ${index + 1}`}
                                required
                            />
                            <button type="button" onClick={() => removeOption(index)} className="remove-option">×</button>
                        </div>
                    ))}
                    <button type="button" onClick={addOption} className="add-option-button">+ Add Option</button>
                </div>
            )}

            <div className="form-group">
                <label>Placeholder Text</label>
                <input
                    type="text"
                    value={formData.placeholder}
                    onChange={(e) => setFormData({...formData, placeholder: e.target.value})}
                    placeholder="Optional placeholder text"
                />
            </div>

            <div className="form-group">
                <label>Help Text</label>
                <textarea
                    value={formData.help_text}
                    onChange={(e) => setFormData({...formData, help_text: e.target.value})}
                    rows="2"
                    placeholder="Optional help text for candidates"
                />
            </div>

            <div className="form-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={formData.is_required}
                        onChange={(e) => setFormData({...formData, is_required: e.target.checked})}
                    />
                    Required Question
                </label>
            </div>

            <div className="form-actions">
                <button type="submit" className="save-button">Save Question</button>
                <button type="button" onClick={onCancel} className="cancel-button">Cancel</button>
            </div>
        </form>
    );
};

const QuestionItem = ({ question, index, onDelete, onEdit, onMoveUp, onMoveDown, questionTypes, canMoveUp, canMoveDown }) => {
    const typeLabel = questionTypes.find(t => t.value === question.question_type)?.label || question.question_type;
    
    return (
        <div className="question-item">
            <div className="question-header">
                <div className="question-controls">
                    <button 
                        onClick={() => onMoveUp(index)} 
                        disabled={!canMoveUp}
                        className="move-button"
                        title="Move up"
                    >
                        ↑
                    </button>
                    <button 
                        onClick={() => onMoveDown(index)} 
                        disabled={!canMoveDown}
                        className="move-button"
                        title="Move down"
                    >
                        ↓
                    </button>
                </div>
                <span className="question-number">{index + 1}</span>
                <span className="question-text">
                    {question.question_text}
                    {question.is_required && <span style={{ color: 'red', marginLeft: '0.25rem', fontWeight: 600 }}>*</span>}
                </span>
                <span className={`required-badge ${question.is_required ? 'required' : 'optional'}`}>
                    {question.is_required ? 'Required' : 'Optional'}
                </span>
                <span className="question-type">{typeLabel}</span>
            </div>
            <div className="question-actions">
                <button onClick={() => onEdit(question)} className="edit-button">Edit</button>
                <button onClick={() => onDelete(question.id)} className="delete-button">Delete</button>
            </div>
        </div>
    );
};

const ApplicationFormPreview = ({ jobId, jobTitle, questions }) => {
    const [formSettings, setFormSettings] = useState(null);

    useEffect(() => {
        fetchFormSettings();
    }, [jobId]);

    const fetchFormSettings = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/form-settings`, {
            });
            if (response.ok) {
                const data = await response.json();
                setFormSettings(data);
            }
        } catch (error) {
            console.error('Error fetching form settings:', error);
        }
    };

    if (!formSettings) {
        return <div>Loading preview...</div>;
    }

    return (
        <div className="form-preview">
            <div 
                className="preview-form"
                style={{
                    backgroundColor: formSettings.form_background_color || '#FFFFFF',
                    color: formSettings.form_text_color || '#000000'
                }}
            >
                {formSettings.form_show_job_title && jobTitle && (
                    <h2>{jobTitle}</h2>
                )}

                <div className="preview-field">
                    <h3 style={{ marginTop: 0 }}>Start your application</h3>
                    <small>First name *, Last name *, Email *</small>
                </div>

                {(() => {
                    const { pre, post } = splitQuestionsForApplyPreview(questions);
                    const preMain = pre.filter((q) => q.question_text !== 'Cover letter (optional)');
                    const cover = pre.filter((q) => q.question_text === 'Cover letter (optional)');
                    return (
                        <>
                            {preMain.map((question) => (
                                <div key={question.id} className="preview-field">
                                    <label>
                                        {question.question_text}
                                        {question.is_required && <span className="required-star"> *</span>}
                                    </label>
                                    {renderPreviewInput(question)}
                                </div>
                            ))}

                            <div className="preview-resume-field">
                                <label>Resume * (Required)</label>
                                <input type="file" disabled />
                                <small>After name, email, phone, city &amp; state</small>
                            </div>

                            {cover.map((question) => (
                                <div key={question.id} className="preview-field">
                                    <label>
                                        {question.question_text}
                                        {question.is_required && <span className="required-star"> *</span>}
                                    </label>
                                    {renderPreviewInput(question)}
                                </div>
                            ))}

                            {formSettings.form_show_linkedin_field && (
                                <div className="preview-field">
                                    <label>LinkedIn Profile URL</label>
                                    <input type="url" disabled placeholder="https://linkedin.com/in/..." />
                                </div>
                            )}

                            {post.map((question) => (
                                <div key={question.id} className="preview-field">
                                    <label>
                                        {question.question_text}
                                        {question.is_required && <span className="required-star"> *</span>}
                                    </label>
                                    {renderPreviewInput(question)}
                                    {question.help_text && question.help_text.length < 160 && (
                                        <small className="help-text">{question.help_text}</small>
                                    )}
                                </div>
                            ))}
                        </>
                    );
                })()}

                <button 
                    className="preview-submit-button"
                    style={{ backgroundColor: formSettings.form_primary_color || '#667eea' }}
                    disabled
                >
                    Submit Application
                </button>
            </div>
        </div>
    );
};

const renderPreviewInput = (question) => {
    switch (question.question_type) {
        case 'number':
            return <input type="number" disabled placeholder={question.placeholder} />;
        case 'multiple_choice':
            return (
                <div className="preview-radio-group">
                    {question.options?.map((option, idx) => (
                        <label key={idx}>
                            <input type="radio" name={`preview_${question.id}`} disabled />
                            {option}
                        </label>
                    ))}
                </div>
            );
        case 'dropdown':
            return (
                <select disabled>
                    <option value="">{question.placeholder || 'Select an option'}</option>
                    {question.options?.map((option, idx) => (
                        <option key={idx} value={option}>{option}</option>
                    ))}
                </select>
            );
        case 'checkbox':
            return (
                <div className="preview-checkbox-group">
                    {question.options?.map((option, idx) => (
                        <label key={idx}>
                            <input type="checkbox" disabled />
                            {option}
                        </label>
                    ))}
                </div>
            );
        default:
            return <input type="text" disabled placeholder={question.placeholder} />;
    }
};

export default ApplicationQuestionsManager;
